import React, { useCallback, useEffect, useState } from 'react';

import { Brain, ClipboardCheck, Loader2, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ROUTES } from '../../constants';
import { useDistrictAiScoringAssist } from '../../hooks/useDistrictAiScoringAssist';
import {
  isRunActive,
  pickTranscriptionVideoId,
  runStatusLabel,
} from '../../services/aiScoringPipelineUtils';
import {
  generateComponentDrafts,
  getAiScoringRun,
  listAiScoringRuns,
  triggerTranscription,
  type AIScoringRunSummary,
} from '../../services/aiScoringService';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';
import { Card, CardContent, CardHeader } from '../common/Card';

import type { VideoUpload } from '../../services/evaluationService';

interface AiScoringPipelineCardProps {
  evaluationId: string;
  canManage: boolean;
  videos: VideoUpload[];
}

export const AiScoringPipelineCard: React.FC<AiScoringPipelineCardProps> = ({
  evaluationId,
  canManage,
  videos,
}) => {
  const navigate = useNavigate();
  const { enabled: aiAssistEnabled, loading: aiAssistLoading } =
    useDistrictAiScoringAssist();
  const [runs, setRuns] = useState<AIScoringRunSummary[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [generatingDrafts, setGeneratingDrafts] = useState(false);
  const [draftCount, setDraftCount] = useState<number | null>(null);

  const transcriptionVideoId = pickTranscriptionVideoId(videos);
  const latestRun = runs[0] ?? null;
  const activeRunId = latestRun?.id;
  const activeRunStatus = latestRun?.status;

  const refreshRuns = useCallback(async () => {
    try {
      setLoadingRuns(true);
      setRuns(await listAiScoringRuns(evaluationId));
    } catch {
      toast.error('Could not load AI transcription status');
    } finally {
      setLoadingRuns(false);
    }
  }, [evaluationId]);

  useEffect(() => {
    if (!aiAssistEnabled) return;
    void refreshRuns();
  }, [aiAssistEnabled, refreshRuns]);

  useEffect(() => {
    if (!activeRunId || !activeRunStatus || !isRunActive(activeRunStatus)) {
      return undefined;
    }

    let cancelled = false;
    const poll = async () => {
      try {
        const updated = await getAiScoringRun(evaluationId, activeRunId);
        if (cancelled) return;
        setRuns(prev => {
          if (prev.length === 0) return [updated];
          const rest = prev.filter(r => r.id !== updated.id);
          return [updated, ...rest];
        });
      } catch {
        // Keep polling; transient errors are common during long jobs.
      }
    };

    const timer = setInterval(() => void poll(), 3000);
    void poll();
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [evaluationId, activeRunId, activeRunStatus]);

  const handleStartTranscription = async () => {
    if (transcriptionVideoId === null) {
      toast.warn('Upload and process a lesson video before transcribing.');
      return;
    }
    try {
      setTriggering(true);
      const run = await triggerTranscription(evaluationId, {
        video_upload_id: transcriptionVideoId,
        language: 'en',
      });
      setRuns(prev => [run, ...prev.filter(r => r.id !== run.id)]);
      toast.success('Transcription queued');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to start transcription';
      toast.error(message);
    } finally {
      setTriggering(false);
    }
  };

  const handleGenerateDrafts = async () => {
    if (!latestRun || latestRun.status.toLowerCase() !== 'completed') {
      toast.warn('Complete transcription before generating drafts.');
      return;
    }
    try {
      setGeneratingDrafts(true);
      const drafts = await generateComponentDrafts(evaluationId, latestRun.id);
      setDraftCount(drafts.length);
      toast.success(
        drafts.length > 0
          ? `Generated ${drafts.length} component draft(s)`
          : 'No drafts returned — check LLM configuration'
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to generate AI drafts';
      toast.error(message);
    } finally {
      setGeneratingDrafts(false);
    }
  };

  if (aiAssistLoading || !aiAssistEnabled) {
    return null;
  }

  const scorePath = ROUTES.EVALUATION_SCORE.replace(':id', evaluationId);
  const canStartTranscription =
    canManage &&
    transcriptionVideoId !== null &&
    !(latestRun && isRunActive(latestRun.status));
  const canGenerateDrafts =
    canManage && latestRun?.status.toLowerCase() === 'completed';

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">AI-Assisted Rubric Drafts</h2>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void refreshRuns()}
            disabled={loadingRuns}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600">
          Transcribe the lesson recording, generate Danielson component draft
          scores (domains 2–3), then review and accept them on the scoring page.
        </p>

        {transcriptionVideoId === null ? (
          <p className="text-sm text-amber-700">
            Upload a lesson video and wait for processing to finish before
            starting transcription.
          </p>
        ) : (
          <p className="text-xs text-gray-500">
            Transcription will use the most recently processed lesson video on
            file for this teacher.
          </p>
        )}

        {loadingRuns && runs.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading transcription runs…
          </div>
        ) : latestRun ? (
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-900">
                Latest run
              </span>
              <Badge>{runStatusLabel(latestRun.status)}</Badge>
              {latestRun.segment_count > 0 && (
                <span className="text-xs text-gray-500">
                  {latestRun.segment_count} segment
                  {latestRun.segment_count === 1 ? '' : 's'}
                </span>
              )}
            </div>
            {latestRun.error_message && (
              <p className="mt-2 text-sm text-red-600">
                {latestRun.error_message}
              </p>
            )}
            {draftCount !== null && draftCount > 0 && (
              <p className="mt-2 text-sm text-green-700">
                {draftCount} draft(s) saved for this run.
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-600">No transcription runs yet.</p>
        )}

        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => void handleStartTranscription()}
              disabled={!canStartTranscription || triggering}
            >
              {triggering ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Brain className="mr-2 h-4 w-4" />
              )}
              Start transcription
            </Button>
            <Button
              variant="secondary"
              onClick={() => void handleGenerateDrafts()}
              disabled={!canGenerateDrafts || generatingDrafts}
            >
              {generatingDrafts ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Brain className="mr-2 h-4 w-4" />
              )}
              Generate AI drafts
            </Button>
            <Button variant="secondary" onClick={() => navigate(scorePath)}>
              <ClipboardCheck className="mr-2 h-4 w-4" />
              Open scoring page
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AiScoringPipelineCard;
